import { Typography } from '@euler/app/components/typography';
import { SearchNavHeader } from '@euler/app/flows/task/components/SearchNavHeader';
import { QuotationCatalogSubjectCell } from '@euler/app/flows/task/pages/quotation/components/QuotationCatalogSubjectCell';
import { useQuotationContext } from '@euler/app/flows/task/pages/quotation/components/QuotationContext';
import { QuotationSubjectInfo } from '@euler/app/flows/task/pages/quotation/types/QuotationCatalog';
import { Center } from '@euler/components/Center';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { StackNavigationOptions } from '@react-navigation/stack';
import { memo, useCallback, useMemo, useState } from 'react';
import { FlatList, KeyboardAvoidingView } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BehaviorSubject } from 'rxjs';
import { useTheme } from 'styled-components';

export const QuotationCatalogSearchPage = wrapNavigatorScreen(
  ({
    selection$,
    toggleQuotationSubject,
  }: {
    selection$: BehaviorSubject<string[]>;
    toggleQuotationSubject: (name: string) => void;
  }) => {
    const insets = useSafeAreaInsets();
    const { rpx } = useResponsiveContext();
    const [searchText, setSearchText] = useState<string>();

    const { catalog } = useQuotationContext();

    const [selection] = useBehaviorSubject(selection$);

    const subjects = useMemo<QuotationSubjectInfo[]>(() => {
      const result: QuotationSubjectInfo[] = [];
      const set = new Set<string>();
      for (const category of catalog.categories) {
        for (const group of category.groups) {
          for (const subject of group.subjects) {
            if (!set.has(subject.name)) {
              result.push(subject);
              set.add(subject.name);
            }
          }
        }
      }
      return result;
    }, [catalog]);

    const onSearchInputTextChange = useCallback((text: string) => {
      setSearchText(text);
    }, []);

    const matchedItems = useMemo(() => {
      const keyword = searchText?.trim();
      if (!keyword) return [];
      const pattern = keyword.toLowerCase();
      return subjects.filter(
        x => x.name.includes(pattern) || x.pyInitial?.includes(pattern),
      );
    }, [searchText, subjects]);

    const onSubjectPress = useCallback(
      (subject: QuotationSubjectInfo) => {
        toggleQuotationSubject(subject.name);
      },
      [toggleQuotationSubject],
    );

    return (
      <KeyboardAvoidingView
        behavior="padding"
        css={`
          flex: 1;
        `}
      >
        <SearchNavHeader
          searchInputEditable
          searchText={searchText}
          searchPlaceholder={'搜索报价项目'}
          onSearchInputChange={onSearchInputTextChange}
        />
        <FlatList
          keyboardShouldPersistTaps="handled"
          data={matchedItems}
          css={`
            flex: 1;
          `}
          contentContainerStyle={{
            flexGrow: 1,
            minHeight: 0,
            paddingBottom: Math.max(insets.bottom, rpx(15)),
          }}
          renderItem={({ item, index }) => (
            <Cell
              item={item}
              selected={selection.includes(item.name)}
              onPress={onSubjectPress}
              first={index === 0}
              last={index === matchedItems.length - 1}
            />
          )}
          ListHeaderComponent={
            !matchedItems.length ? (
              <Center
                css={`
                  padding: ${rpx(32)}px;
                  height: ${rpx(200)}px;
                `}
              >
                <Typography.Label light color="#555">
                  {searchText?.trim().length
                    ? '未找到报价项目? 试试调整搜索关键词，支持按拼音首字母或项目中文名称搜索'
                    : '支持按拼音首字母或项目中文名称搜索'}
                </Typography.Label>
              </Center>
            ) : null
          }
        />
      </KeyboardAvoidingView>
    );
  },
  {
    cardOverlayEnabled: true,
    cardStyle: { backgroundColor: 'white' },
    // animationEnabled: false,
    headerShown: false,
    // gestureEnabled: false,
  } as StackNavigationOptions,
);

const Cell = memo(
  ({
    item,
    selected,
    onPress,
    last,
  }: {
    first?: boolean;
    last?: boolean;
    item: QuotationSubjectInfo;
    selected: boolean;
    onPress: (item: QuotationSubjectInfo) => void;
  }) => {
    const { rpx } = useResponsiveContext();

    const {
      construction: {
        schedule: {
          workItem: {
            cell: { textLight, bgLight },
          },
        },
      },
    } = useTheme();

    const onCellPress = useCallback(() => {
      onPress?.(item);
    }, [item, onPress]);

    return (
      <QuotationCatalogSubjectCell
        title={item.name}
        selected={selected}
        onPress={onCellPress}
        onAdd={onCellPress}
        onRemove={onCellPress}
        titleStyle={{
          color: textLight,
          fontFamily: FontFamily.NotoSans.Light,
          fontSize: rpx(15),
        }}
        css={`
          background-color: ${bgLight};
          padding-top: ${rpx(5)}px;
          padding-bottom: ${rpx(5)}px;
          padding-left: ${rpx(15)}px;
          padding-right: ${rpx(15)}px;
          margin: 0;
          border-bottom-width: 1px;
          border-bottom-color: ${last ? 'transparent' : '#eee'};
          border-radius: 0;
        `}
      />
    );
  },
);
